import React, { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DynamicTitle from "./DynamicTitle";
import { trackEvent, identifyNewUser } from "../utils/posthog";
import { useAuth } from "../contexts/AuthContext";

function AccountSetup() {
  const [checkingToken, setCheckingToken] = useState(true);
  const [displayForm, setDisplayForm] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isExpired, setIsExpired] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchUser } = useAuth();

  const useQuery = () => new URLSearchParams(location.search);
  const query = useQuery();
  const token = query.get("token");
  const email = query.get("email");
  const emailEncoded = encodeURIComponent(email || "");

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.get(`/api/account-setup?token=${token}&email=${emailEncoded}`);
        setCheckingToken(false);
        setDisplayForm(true);
        setIsExpired(false);
      } catch (error) {
        setCheckingToken(false);
        const status = error.response?.status || 500;
        const errorMsg = error.response?.data?.error || error.message;
        setErrorMessage(handleError(status, errorMsg));
        setIsExpired(status === 401);
      }
    };

    if (token && email) {
      fetchData();
    } else {
      setCheckingToken(false);
      setErrorMessage("Missing token or email. Please use the link from your email.");
      setIsExpired(true);
    }
  }, [email, emailEncoded, token]);

  function handleError(code, errMsg) {
    if (code === 401) return "This link has expired. Click below to get a new one.";
    if (code === 404) return "We couldn't find your account. Please contact support.";
    if (code === 400) return errMsg || "Invalid request. Please check your information and try again.";
    if (code >= 400 && code < 500) return errMsg || "Your request could not be processed. Please try again.";
    return "An error occurred. Please try again later.";
  }

  const handleResendMagicLink = async () => {
    if (!email) {
      toast.error("Email address is required to resend the link.");
      return;
    }

    setResendingLink(true);
    setResendSuccess(false);
    setErrorMessage("");

    try {
      await axios.post("/api/send-magic-link", { email, kind: "signup" });
      toast.success("We've sent a new link to your email.");
      setResendSuccess(true);
      setMessage("A new link has been sent to your email. Please check your inbox.");
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Failed to send link. Please try again.";
      toast.error(errorMsg);
      setErrorMessage(errorMsg);
    } finally {
      setResendingLink(false);
    }
  };

  const validatePassword = (pwd) => /^(?=.*[0-9])(?=.*[!@#$%^&*? ]).{8,}$/.test(pwd);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 8 characters long and include a number and a symbol.");
      return;
    } else {
      setPasswordError("");
    }

    if (password !== passwordConfirm) {
      setPasswordMatchError("Passwords should match.");
      return;
    } else {
      setPasswordMatchError("");
    }

    try {
      setDisplayForm(false);
      setMessage("Setting up your account...");
      const encodedToken = encodeURIComponent(token || "");
      const encodedEmail = encodeURIComponent(email || "");
      const url = `/api/account-setup?token=${encodedToken}&email=${encodedEmail}`;

      let response;
      try {
        response = await axios.put(url, { password });
      } catch (putError) {
        if (putError.response?.status === 405) {
          response = await axios.post(url, { password });
        } else {
          throw putError;
        }
      }

      identifyNewUser(email, { signup_source: 'stripe_trial' });
      trackEvent('account_setup_completed', { signup_source: 'stripe_trial' });

      const { token: sessionToken } = response.data;
      if (sessionToken) {
        localStorage.setItem('token', sessionToken);
        await fetchUser();
      }

      toast.success("Welcome to RealignMind!");
      setMessage("You're all set. Taking you to the app...");
      setTimeout(() => navigate(sessionToken ? '/' : '/login'), 1500);
    } catch (error) {
      setDisplayForm(true);
      setMessage("");
      const status = error.response?.status || 500;
      const errorMsg = error.response?.data?.error || error.message;
      setErrorMessage(handleError(status, errorMsg));
      toast.error(errorMsg || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Set Up Your Account</DynamicTitle>
      <Header />
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-text mb-2">
              You're almost in
            </h1>
            <p className="text-textSecondary">
              Create a password to access your RealignMind account
            </p>
          </div>

          <div className="bg-white py-8 px-6 rounded-lg border border-tint">
            {checkingToken && (
              <div className="text-center py-4">
                <p className="text-textSecondary">Checking your link...</p>
              </div>
            )}

            {message && (
              <div className="bg-statusOk/10 border border-statusOk/30 text-statusOk px-4 py-3 rounded-md mb-4">
                {message}
              </div>
            )}

            {errorMessage && (
              <div className="bg-statusError/10 border border-statusError/30 text-statusError px-4 py-3 rounded-md mb-4">
                {errorMessage}
                {isExpired && (
                  <div className="mt-3">
                    <button
                      onClick={handleResendMagicLink}
                      disabled={resendingLink}
                      className="text-sm font-medium text-accent hover:underline disabled:text-textSecondary/50 disabled:no-underline"
                    >
                      {resendingLink ? "Sending..." : "Send me a new link"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {resendSuccess && (
              <div className="bg-statusOk/10 border border-statusOk/30 text-statusOk px-4 py-3 rounded-md mb-4">
                {message || "A new link has been sent to your email. Please check your inbox."}
              </div>
            )}

            {displayForm && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-text mb-1"
                  >
                    Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                  />
                  {passwordError && (
                    <p className="mt-1 text-sm text-statusError">{passwordError}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-text mb-1"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                  />
                  {passwordMatchError && (
                    <p className="mt-1 text-sm text-statusError">{passwordMatchError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-accent hover:bg-accentDark text-white font-semibold py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
                >
                  Set up my account
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-textSecondary hover:text-accent transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-textSecondary">
              Secure • Private • Research-backed
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default AccountSetup;
